import clsx from "clsx";
import { motion } from "framer-motion";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC } from "react";

import { Cta } from "../../buttons/cta";
import { Icon } from "../../icon";

export interface BannerImageProps extends Queries.BannerImageDataFragment {}

export const BannerImage: FC<BannerImageProps> = ({
  backgroundImage,
  topTitle,
  bottomTitle,
  withClickableTitle = true,
  cta,
}) => {
  const title = "text-xl md:text-2xl";
  const descriptionTextSize = "text-4xl xl:text-5xl !leading-tight max-w-2xl";

  return (
    <div className="bg-neutral min-h-[80vh] md:min-h-0 p-0 md:p-10 lg:p-20 flex justify-center">
      <div className="relative flex-1 max-w-7xl h-160">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="md:mask-shape overflow-hidden w-full h-full"
        >
          <div className="bg-black opacity-50 absolute z-10 top-0 left-0 w-full h-full" />
          {backgroundImage?.gatsbyImageData && (
            <GatsbyImage
              image={backgroundImage.gatsbyImageData}
              objectFit="cover"
              alt=""
              className="w-full h-full absolute"
            />
          )}
        </motion.div>
        <div
          className={clsx(
            "absolute z-20 top-0 left-0 w-full h-full break-words",
            "p-8 pt-20 md:pt-8",
            "flex flex-col md:justify-center items-center"
          )}
        >
          {topTitle && (
            <motion.h2
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className={clsx(
                "text-white font-medium text-center",
                withClickableTitle ? title : descriptionTextSize,
                "mb-10 md:mb-5"
              )}
            >
              {topTitle}
            </motion.h2>
          )}
          {bottomTitle && (
            <motion.div
              initial={{ opacity: 0, y: 200 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className={clsx(
                "text-white font-medium text-center",
                withClickableTitle ? descriptionTextSize : title
              )}
            >
              {withClickableTitle && cta ? (
                <Cta {...cta} className="group">
                  <h3 className="relative">
                    {bottomTitle}
                    <Icon
                      icon="arrow-right"
                      size={40}
                      className={clsx(
                        "block md:inline",
                        "mt-2 md:mt-0 ml-auto mr-auto md:ml-2 md:mr-0",
                        "transition-all duration-300 group-hover:translate-x-2"
                      )}
                    />
                  </h3>
                </Cta>
              ) : (
                <>
                  <h3 className="relative">{bottomTitle}</h3>
                </>
              )}
            </motion.div>
          )}
          {!withClickableTitle && (
            <motion.div
              initial={{ opacity: 0, y: 200 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            >
              {cta && <Cta {...cta} variant="primary-inverted" buttonClassName="mt-8"></Cta>}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export const query = graphql`
  fragment BannerImageData on ContentfulBannerImage {
    __typename
    topTitle
    bottomTitle
    backgroundImage {
      gatsbyImageData
    }
    withClickableTitle
    cta {
      ...CtaData
    }
  }
`;
